// @flow
import * as React from 'react';
import Radium from 'radium';
import moment from 'moment';
import classNames from 'classnames';
import { Button, Card, CardHeader, CardBlock } from 'reactstrap';
import { compose, withState } from 'recompose';
import { Spacing, Colors } from 'utils/styleguide';

import PreviewImage from 'components/PreviewImage';
import ImageUpload from 'components/ImageUpload';
import TextEditModal from 'components/TextEditModal';

const EDIT_FIELD_MAPPING = {
  title: 'Titel',
  description: 'Beschreibung',
};

type EditField = 'title' | 'description';
type RecomposeProps = {|
  +editField: ?EditField,
  +onEditFieldChange: (editField: ?EditField) => void,
|};

type Props = RecomposeProps & {
  title: string,
  description: string,
  images: Array<string>,
  createdAt: number,
  createdBy?: {
    firstname: string,
    lastname: string,
    email: string,
    isCreatedByManager?: boolean,
  },
  maxImages: number,
  onImageAdded?: (image: string) => void,
  onImageDeleted?: (index: number) => void,
  onFieldChanged?: (field: string, value: string) => void,
};

class Hero extends React.Component<Props> {
  _renderName(name: ?string, isCreatedByManager: boolean) {
    const userIcon = isCreatedByManager ? (
      <i className={classNames('icon-star', 'text-warning')} />
    ) : (
      <i className={classNames('icon-user')} />
    );
    return name ? (
      <span>
        {userIcon} {name}
      </span>
    ) : (
      ''
    );
  }

  _renderEmail(email: ?string) {
    return email ? (
      <span>
        (<a href={`mailto:${email}`}>{email}</a>) -{' '}
      </span>
    ) : (
      ''
    );
  }

  _renderCreatedAt(createdAt: number) {
    return (
      <span>
        <i className={classNames('icon-calendar')} /> erstellt am{' '}
        {moment(createdAt).format('DD. MMMM YYYY - HH:mm')}
      </span>
    );
  }

  render() {
    const {
      title,
      description,
      images,
      createdAt,
      createdBy,
      maxImages,
      onImageAdded,
      onImageDeleted,
      editField,
      onEditFieldChange,
      onFieldChanged,
    } = this.props;

    return (
      <Card style={styles.root}>
        <CardHeader style={styles.title}>
          {title}
          {!!onFieldChanged && (
            <Button
              color="link"
              size="sm"
              onClick={() => onEditFieldChange('title')}
            >
              (bearbeiten)
            </Button>
          )}
        </CardHeader>
        <CardBlock>
          {!!createdBy && (
            <div style={styles.creation}>
              {this._renderName(
                `${createdBy.firstname} ${createdBy.lastname}`,
                createdBy.isCreatedByManager
              )}{' '}
              {this._renderEmail(createdBy.email)}{' '}
              {this._renderCreatedAt(createdAt)}
            </div>
          )}
          <div style={styles.description}>
            {description}
            {!!onFieldChanged && (
              <Button
                color="link"
                size="sm"
                onClick={() => onEditFieldChange('description')}
              >
                (bearbeiten)
              </Button>
            )}
          </div>
        </CardBlock>
        <CardBlock>
          <div style={styles.images}>
            {images.map((image, index) => (
              <div key={image} style={{ marginRight: Spacing.Micro }}>
                <PreviewImage
                  style={{ marginRight: Spacing.Micro }}
                  image={image}
                  onDeleteClicked={
                    !!onImageDeleted ? () => onImageDeleted(index) : undefined
                  }
                />
              </div>
            ))}
            {!!onImageAdded && Object.keys(images).length < maxImages && (
              <div style={{ marginRight: Spacing.Micro }}>
                <ImageUpload onImageSelected={onImageAdded} large />
              </div>
            )}
          </div>
        </CardBlock>
        {!!editField && (
          <TextEditModal
            title={`${EDIT_FIELD_MAPPING[editField]} bearbeiten`}
            inputType={editField === 'title' ? 'textfield' : 'textarea'}
            initialValue={editField === 'title' ? title : description}
            onCancel={() => onEditFieldChange(null)}
            onSubmit={value => {
              if (!!onFieldChanged) {
                onFieldChanged(editField, value);
              }
              onEditFieldChange(null);
            }}
          />
        )}
      </Card>
    );
  }
}

const styles = {
  root: {
    width: '100%',
  },
  title: {
    display: 'flex',
    fontWeight: 'bold',
    justifyContent: 'space-between',
  },
  creation: {
    color: Colors.LightGrey,
    marginBottom: Spacing.Micro,
  },
  images: {
    display: 'flex',
  },
  description: {
    whiteSpace: 'pre-wrap',
  },
};

const HeroContainer = compose(
  Radium,
  withState('editField', 'onEditFieldChange', null)
)(Hero);

export default HeroContainer;



// WEBPACK FOOTER //
// ./src/components/Hero/Hero.js